import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  BellIcon,
  XMarkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { CalendarIcon as CalendarIconSolid } from '@heroicons/react/24/solid';

const EventScheduler = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [availableRooms, setAvailableRooms] = useState([]);
  const [suggestedAttendees, setSuggestedAttendees] = useState([]);
  const [showAttendeeSearch, setShowAttendeeSearch] = useState(false);
  const [attendeeSearchTerm, setAttendeeSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    allDay: false,
    location: '',
    roomId: '',
    category: 'meeting',
    type: 'meeting',
    priority: 'medium',
    recurring: 'none',
    recurringEnd: '',
    reminder: '15min',
    attendees: [],
    isPrivate: false,
    allowGuests: true,
    maxAttendees: '',
    notes: ''
  });

  useEffect(() => {
    // Set default date/time to current
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    
    const formatDate = (date) => date.toISOString().split('T')[0];

    setFormData(prev => ({
      ...prev,
      startDate: formatDate(tomorrow),
      endDate: formatDate(tomorrow),
      startTime: '09:00',
      endTime: '10:00'
    }));

    // Fetch available rooms and attendees
    fetchAvailableRooms();
    fetchSuggestedAttendees();
  }, []);

  const fetchAvailableRooms = () => {
    // Simulate fetching available meeting rooms
    const mockRooms = [
      { id: 'room001', name: 'Conference Room A', capacity: 12, equipment: ['Projector', 'Video Conferencing', 'Whiteboard'] },
      { id: 'room002', name: 'Conference Room B', capacity: 8, equipment: ['Projector', 'Whiteboard'] },
      { id: 'room003', name: 'Meeting Room 101', capacity: 6, equipment: ['TV Display', 'Conference Phone'] },
      { id: 'room004', name: 'Executive Conference Room', capacity: 16, equipment: ['Smart Board', 'Video Conferencing', 'Sound System'] },
      { id: 'room005', name: 'Training Center - Room 1', capacity: 25, equipment: ['Projector', 'Audio System', 'Microphone'] },
      { id: 'room006', name: 'Consultation Room 5', capacity: 4, equipment: ['Computer', 'Medical Equipment'] },
      { id: 'room007', name: 'Auditorium', capacity: 100, equipment: ['Stage', 'Audio/Visual System', 'Lighting'] }
    ];
    setAvailableRooms(mockRooms);
  };

  const fetchSuggestedAttendees = () => {
    // Simulate fetching staff members
    const mockAttendees = [
      { id: 'emp001', name: 'Dr. Sarah Johnson', role: 'Attending Physician', department: 'Internal Medicine', email: 'sarah.johnson@hospital.com' },
      { id: 'emp002', name: 'Dr. Michael Chen', role: 'Resident', department: 'Internal Medicine', email: 'michael.chen@hospital.com' },
      { id: 'emp003', name: 'Nurse Emily Davis', role: 'Head Nurse', department: 'Nursing', email: 'emily.davis@hospital.com' },
      { id: 'emp004', name: 'Dr. Robert Wilson', role: 'Medical Director', department: 'Administration', email: 'robert.wilson@hospital.com' },
      { id: 'emp005', name: 'Jennifer Wu', role: 'HR Manager', department: 'Human Resources', email: 'jennifer.wu@hospital.com' },
      { id: 'emp006', name: 'Lisa Rodriguez', role: 'Operations Manager', department: 'Operations', email: 'lisa.rodriguez@hospital.com' },
      { id: 'emp007', name: 'Dr. Emily Davis', role: 'Senior Physician', department: 'Cardiology', email: 'emily.davis.cardio@hospital.com' },
      { id: 'emp008', name: 'Tech Support Team', role: 'IT Support', department: 'IT', email: 'tech.support@hospital.com' }
    ];
    setSuggestedAttendees(mockAttendees);
  };

  const categories = [
    { id: 'meeting', name: 'Meeting', color: '#3b82f6' },
    { id: 'appointment', name: 'Appointment', color: '#10b981' },
    { id: 'training', name: 'Training', color: '#f59e0b' },
    { id: 'maintenance', name: 'Maintenance', color: '#8b5cf6' },
    { id: 'consultation', name: 'Consultation', color: '#ef4444' },
    { id: 'seminar', name: 'Seminar', color: '#06b6d4' }
  ];

  const eventTypes = [
    { id: 'meeting', name: 'Meeting' },
    { id: 'appointment', name: 'Appointment' },
    { id: 'training', name: 'Training Session' },
    { id: 'seminar', name: 'Seminar/Workshop' },
    { id: 'consultation', name: 'Consultation' },
    { id: 'maintenance', name: 'Maintenance' },
    { id: 'other', name: 'Other' }
  ];

  const priorities = [
    { id: 'low', name: 'Low Priority', color: 'green' },
    { id: 'medium', name: 'Medium Priority', color: 'yellow' },
    { id: 'high', name: 'High Priority', color: 'red' }
  ];

  const recurringOptions = [
    { id: 'none', name: 'Does not repeat' },
    { id: 'daily', name: 'Daily' },
    { id: 'weekly', name: 'Weekly' },
    { id: 'biweekly', name: 'Every 2 weeks' },
    { id: 'monthly', name: 'Monthly' },
    { id: 'quarterly', name: 'Quarterly' },
    { id: 'yearly', name: 'Yearly' }
  ];

  const reminderOptions = [
    { id: 'none', name: 'No reminder' },
    { id: '5min', name: '5 minutes before' },
    { id: '15min', name: '15 minutes before' },
    { id: '30min', name: '30 minutes before' },
    { id: '1hour', name: '1 hour before' },
    { id: '2hours', name: '2 hours before' },
    { id: '1day', name: '1 day before' },
    { id: '1week', name: '1 week before' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // Auto-adjust end time when start time changes
    if (field === 'startTime' && value) {
      const [hours, minutes] = value.split(':');
      const startTime = new Date();
      startTime.setHours(parseInt(hours), parseInt(minutes));
      const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
      
      const endHours = endTime.getHours().toString().padStart(2, '0');
      const endMinutes = endTime.getMinutes().toString().padStart(2, '0');
      
      setFormData(prev => ({
        ...prev,
        endTime: `${endHours}:${endMinutes}`
      }));
    }

    // Auto-set end date when start date changes
    if (field === 'startDate' && value) {
      setFormData(prev => ({
        ...prev,
        endDate: value
      }));
    }
  };

  const addAttendee = (attendee) => {
    if (!formData.attendees.find(a => a.id === attendee.id)) {
      setFormData(prev => ({
        ...prev,
        attendees: [...prev.attendees, attendee]
      }));
    }
    setAttendeeSearchTerm('');
    setShowAttendeeSearch(false);
  };

  const removeAttendee = (attendeeId) => {
    setFormData(prev => ({
      ...prev,
      attendees: prev.attendees.filter(a => a.id !== attendeeId)
    }));
  };

  const filteredAttendees = suggestedAttendees.filter(attendee =>
    attendee.name.toLowerCase().includes(attendeeSearchTerm.toLowerCase()) ||
    attendee.role.toLowerCase().includes(attendeeSearchTerm.toLowerCase()) ||
    attendee.department.toLowerCase().includes(attendeeSearchTerm.toLowerCase())
  );

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Event title is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (!formData.allDay) {
      if (!formData.startTime) {
        newErrors.startTime = 'Start time is required';
      }
      if (!formData.endTime) {
        newErrors.endTime = 'End time is required';
      }

      // Check if end time is after start time
      if (formData.startDate === formData.endDate && formData.startTime && formData.endTime) {
        const start = new Date(`${formData.startDate}T${formData.startTime}`);
        const end = new Date(`${formData.endDate}T${formData.endTime}`);
        if (end <= start) {
          newErrors.endTime = 'End time must be after start time';
        }
      }
    }

    if (formData.recurring !== 'none' && !formData.recurringEnd) {
      newErrors.recurringEnd = 'Recurring end date is required for repeating events';
    }

    if (formData.maxAttendees && isNaN(parseInt(formData.maxAttendees))) {
      newErrors.maxAttendees = 'Maximum attendees must be a number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setShowSuccess(true);
      
      // Reset form after success
      setTimeout(() => {
        navigate('/calendar');
      }, 2000);
      
    } catch (error) {
      console.error('Error creating event:', error);
      setErrors({ submit: 'Failed to create event. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getSelectedRoom = () => {
    return availableRooms.find(room => room.id === formData.roomId);
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-6 sm:p-8 max-w-md w-full text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <CheckIcon className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Event Created Successfully!</h3>
          <p className="text-sm text-gray-500 mb-4">
            Your event "{formData.title}" has been scheduled and invitations will be sent to attendees.
          </p>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-xs text-gray-400 mt-2">Redirecting to calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center">
                <CalendarIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mr-3 flex-shrink-0" />
                <div className="min-w-0">
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Schedule New Event</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    Create a new calendar event and invite attendees
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/calendar')}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          {/* General Information */}
          <div className="bg-white shadow rounded-lg sm:rounded-xl">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-medium text-gray-900">Event Details</h2>
            </div>
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`block w-full border rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 ${
                    errors.title ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  placeholder="Enter event title..."
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  rows="3"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter event description..."
                />
              </div>

              {/* Category and Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {eventTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority Level
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {priorities.map(priority => (
                    <label key={priority.id} className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="priority"
                        value={priority.id}
                        checked={formData.priority === priority.id}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700 flex-1">{priority.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Date and Time */}
          <div className="bg-white shadow rounded-lg sm:rounded-xl">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-medium text-gray-900 flex items-center">
                <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
                Date & Time
              </h2>
            </div>
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* All Day Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allDay"
                  checked={formData.allDay}
                  onChange={(e) => handleInputChange('allDay', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="allDay" className="ml-2 text-sm text-gray-700">
                  All day event
                </label>
              </div>

              {/* Start Date/Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className={`block w-full border rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 ${
                      errors.startDate ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                  {errors.startDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
                  )}
                </div>

                {!formData.allDay && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time *
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => handleInputChange('startTime', e.target.value)}
                      className={`block w-full border rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 ${
                        errors.startTime ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                    />
                    {errors.startTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.startTime}</p>
                    )}
                  </div>
                )}
              </div>

              {/* End Date/Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className={`block w-full border rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 ${
                      errors.endDate ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                  {errors.endDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
                  )}
                </div>

                {!formData.allDay && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time *
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => handleInputChange('endTime', e.target.value)}
                      className={`block w-full border rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 ${
                        errors.endTime ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                    />
                    {errors.endTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.endTime}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Recurring Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Repeat
                </label>
                <select
                  value={formData.recurring}
                  onChange={(e) => handleInputChange('recurring', e.target.value)}
                  className="block w-full sm:w-1/2 border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {recurringOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              {formData.recurring !== 'none' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Repeat Until *
                  </label>
                  <input
                    type="date"
                    value={formData.recurringEnd}
                    onChange={(e) => handleInputChange('recurringEnd', e.target.value)}
                    className={`block w-full sm:w-1/2 border rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 ${
                      errors.recurringEnd ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                  {errors.recurringEnd && (
                    <p className="mt-1 text-sm text-red-600">{errors.recurringEnd}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <MapPinIcon className="h-5 w-5 mr-2" />
                Location
              </h2>
            </div>
            <div className="p-6 space-y-6">
              {/* Room Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Room
                </label>
                <select
                  value={formData.roomId}
                  onChange={(e) => handleInputChange('roomId', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a meeting room...</option>
                  {availableRooms.map(room => (
                    <option key={room.id} value={room.id}>
                      {room.name} (Capacity: {room.capacity})
                    </option>
                  ))}
                </select>
                {getSelectedRoom() && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-md">
                    <div className="text-sm">
                      <p className="font-medium text-blue-900">{getSelectedRoom().name}</p>
                      <p className="text-blue-700">Capacity: {getSelectedRoom().capacity} people</p>
                      <p className="text-blue-700">
                        Equipment: {getSelectedRoom().equipment.join(', ')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter custom location (overrides room selection)..."
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to use selected meeting room, or enter custom location
                </p>
              </div>
            </div>
          </div>

          {/* Attendees */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <UserGroupIcon className="h-5 w-5 mr-2" />
                Attendees
              </h2>
            </div>
            <div className="p-6 space-y-6">
              {/* Add Attendees */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Invite Attendees
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowAttendeeSearch(!showAttendeeSearch)}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Attendee
                  </button>
                </div>

                {showAttendeeSearch && (
                  <div className="mb-4">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={attendeeSearchTerm}
                        onChange={(e) => setAttendeeSearchTerm(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Search staff members..."
                      />
                    </div>
                    
                    {attendeeSearchTerm && (
                      <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                        {filteredAttendees.map(attendee => (
                          <button
                            key={attendee.id}
                            type="button"
                            onClick={() => addAttendee(attendee)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                          >
                            <div>
                              <div className="text-sm font-medium text-gray-900">{attendee.name}</div>
                              <div className="text-sm text-gray-500">{attendee.role} - {attendee.department}</div>
                            </div>
                            <PlusIcon className="h-4 w-4 text-gray-400" />
                          </button>
                        ))}
                        {filteredAttendees.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">No staff members found</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Attendees */}
                {formData.attendees.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-gray-700">Selected Attendees:</span>
                    {formData.attendees.map(attendee => (
                      <div key={attendee.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{attendee.name}</div>
                          <div className="text-sm text-gray-500">{attendee.role}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttendee(attendee.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Event Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Attendees
                  </label>
                  <input
                    type="number"
                    value={formData.maxAttendees}
                    onChange={(e) => handleInputChange('maxAttendees', e.target.value)}
                    className={`block w-full border rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 ${
                      errors.maxAttendees ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="No limit"
                  />
                  {errors.maxAttendees && (
                    <p className="mt-1 text-sm text-red-600">{errors.maxAttendees}</p>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allowGuests"
                      checked={formData.allowGuests}
                      onChange={(e) => handleInputChange('allowGuests', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="allowGuests" className="ml-2 text-sm text-gray-700">
                      Allow guests to be invited
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isPrivate"
                      checked={formData.isPrivate}
                      onChange={(e) => handleInputChange('isPrivate', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isPrivate" className="ml-2 text-sm text-gray-700">
                      Private event (details hidden from non-attendees)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <BellIcon className="h-5 w-5 mr-2" />
                Notifications & Reminders
              </h2>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder
                </label>
                <select
                  value={formData.reminder}
                  onChange={(e) => handleInputChange('reminder', e.target.value)}
                  className="block w-full md:w-1/2 border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {reminderOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Any additional notes or instructions for attendees..."
                />
              </div>
            </div>
          </div>

          {/* Form Errors */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">Error</p>
                  <p className="text-sm text-red-700">{errors.submit}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={() => navigate('/calendar')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Event...
                </>
              ) : (
                <>
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Create Event
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventScheduler;
